import { useState } from 'react';
import { getLeaves } from 'react-mosaic-component';
import type { MosaicNode } from 'react-mosaic-component';
import { Check, LayoutGrid, Loader2, Plus, RotateCcw, Save, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { type TileId, type SaveStatus, ALL_TILE_IDS, TILE_TITLES } from './types';

interface AddPanelButtonProps {
  mosaicTree: MosaicNode<TileId> | null;
  onTreeChange: (tree: MosaicNode<TileId>) => void;
  onReset: () => void;
  onSave: () => void;
  saveStatus: SaveStatus;
  isDirty: boolean;
}

function addTileToTree(currentTree: MosaicNode<TileId> | null, newTile: TileId): MosaicNode<TileId> {
  if (!currentTree) return newTile;
  return {
    direction: 'column',
    first: currentTree,
    second: newTile,
    splitPercentage: 75,
  };
}

const SAVE_ICON: Record<SaveStatus, React.ReactNode> = {
  idle: <Save className="h-4 w-4" />,
  saving: <Loader2 className="h-4 w-4 animate-spin" />,
  saved: <Check className="h-4 w-4" />,
  error: <X className="h-4 w-4" />,
};

const SAVE_LABEL: Record<SaveStatus, string> = {
  idle: 'Save Layout',
  saving: 'Saving…',
  saved: 'Saved',
  error: 'Save failed',
};

export function AddPanelButton({ mosaicTree, onTreeChange, onReset, onSave, saveStatus, isDirty }: AddPanelButtonProps) {
  const [open, setOpen] = useState(false);

  const openTiles = new Set(getLeaves(mosaicTree));
  const closedTiles = ALL_TILE_IDS.filter((id) => !openTiles.has(id));
  const hasClosedTiles = closedTiles.length > 0;

  const handleAdd = (tileId: TileId) => {
    onTreeChange(addTileToTree(mosaicTree, tileId));
    setOpen(false);
  };

  return (
    <div className="absolute bottom-4 right-4 z-50 flex items-center gap-2">
      <Button
        size="sm"
        variant="secondary"
        className="shadow-md gap-1.5"
        onClick={onReset}
        aria-label="Reset Layout"
        title="Reset to default layout"
      >
        <RotateCcw className="h-4 w-4" />
      </Button>
      <Button
        size="sm"
        variant={saveStatus === 'saved' ? 'default' : saveStatus === 'error' ? 'destructive' : 'secondary'}
        className="shadow-md gap-1.5"
        onClick={onSave}
        disabled={saveStatus === 'saving' || !isDirty}
        aria-label={SAVE_LABEL[saveStatus]}
        title={isDirty ? SAVE_LABEL[saveStatus] : 'Layout already saved'}
      >
        {SAVE_ICON[saveStatus]}
      </Button>
      <Popover open={hasClosedTiles && open} onOpenChange={(next) => hasClosedTiles && setOpen(next)}>
        <PopoverTrigger asChild>
          <Button
            size="sm"
            variant="secondary"
            className="shadow-md gap-1.5"
            disabled={!hasClosedTiles}
            aria-label="Panels"
          >
            <LayoutGrid className="h-4 w-4" />
            Panels
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-48 p-2">
          <div className="space-y-1">
            {closedTiles.map((tileId) => (
              <Button
                key={tileId}
                variant="ghost"
                size="sm"
                className="w-full justify-start gap-2 text-xs h-8"
                onClick={() => handleAdd(tileId)}
                aria-label={`Add ${TILE_TITLES[tileId]}`}
              >
                <Plus className="h-3.5 w-3.5" />
                {TILE_TITLES[tileId]}
              </Button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
